<template>
  <div class="info">
    <div class="settings-container">
      <!-- CARD — Informações do Tenant -->
      <div class="settings-card">
        <div class="card-header justify-between">
          <div class="flex align-items-center">
            <feather-icon class="card-icon" icon="InfoIcon"/>
            <span class="card-title">Informações do Tenant</span>
          </div>

          <div class="inline-flex gap-2">
            <vx-tooltip class="cursor-pointer" delay=".2s" color="gray" position="bottom" v-if="canSee('reactivate') && tenantData && isOriginBackoffice" :text="tenantData.deleted_at === null ? 'Desativar' : 'Reativar'">
              <vs-icon @click="confirmAction()" :icon="tenantData.deleted_at === null ? 'delete' : 'restore'"></vs-icon>
            </vx-tooltip>
            <vx-tooltip v-if="tenantData" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Atualizar Dados">
              <feather-icon @click="loadTenantData()" icon="RefreshCwIcon"/>
            </vx-tooltip>
          </div>
        </div>
        <div class="card-divider"></div>
        <div class="card-content">
          <div v-if="tenantData">
            <div class="tenant-info-grid">
              <div class="info-item">
                <div class="info-label">Nome</div>
                <div class="info-value">{{ tenantData.fqdn.split('.')[0] }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">CNPJ</div>
                <div class="info-value">{{ tenantData.cnpj || 'N/A' }}</div>
              </div>
              <div class="info-item" v-if="tenantData.created_at">
                <div class="info-label">Criado em</div>
                <div class="info-value">{{ new Date(tenantData.created_at).toLocaleString('pt-BR') }}</div>
              </div>
              <div class="info-item" v-if="tenantData.deleted_at">
                <div class="info-label">Desativado em</div>
                <div class="info-value">{{ new Date(tenantData.deleted_at).toLocaleString('pt-BR') }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Plano</div>
                <div class="info-value">{{ tenantData.plan || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Beta</div>
                <div class="info-value">
                  <span :class="tenantData.is_beta ? 'status-active' : 'status-inactive'">
                    {{ tenantData.is_beta ? 'Sim' : 'Não' }}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Manutenção</div>
                <div class="info-value">
                  <span :class="tenantData.is_in_maintenance ? 'status-warning' : 'status-inactive'">
                    {{ tenantData.is_in_maintenance ? 'Sim' : 'Não' }}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Limite de Usuários</div>
                <div class="info-value">{{ tenantData.user_limit || 'N/A' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Limite de Atendimentos</div>
                <div class="info-value">{{ tenantData.ticket_limit || 'N/A' }}</div>
              </div>
              <div class="info-item" v-if="tenantData && !tenantData.deleted_at && !wrongBackoffice">
                <div class="info-label">Plataforma</div>
                <div class="info-value">
                  <vs-button type="border" size="small" @click=redirectToLink()> Acessar </vs-button>
                </div>
              </div>
              <div class="info-item" v-if="tenantData && !tenantData.deleted_at && wrongBackoffice">
                <div class="info-label">Informativo</div>
                <div class="info-value"><span class="status-warning" >Esse cliente é Beta. <br> Acesse o Backoffice de Beta para gerenciar.</span></div>
              </div>
            </div>
          </div>
          <div v-else-if="shouldDisableCards" class="card-content disabled-content">
            <p class="disabled-message">Esta funcionalidade está indisponível.</p>
          </div>
        </div>
      </div>

      <!-- CARD — Recursos -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header">
          <feather-icon class="card-icon" icon="SettingsIcon"/>
          <span class="card-title">Recursos da Plataforma</span>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <UpdateTenantModal
            :tenant="tenantData"
            :isPopup="false"
            :isActive="false"
            :features="dashSettings.activeFeatures"
            :enabledPlan="dashSettings.enabledPlan"
            :defaultPlans="dashSettings.defaultPlans"
            @setState="(newVal, features) => setState(newVal, features, null)"
            @setPlan="updatePlan"
          />
        </div>
        <div v-else class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>

      <!-- CARD — Conexões -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header justify-between">
          <div class="flex align-items-center">
            <feather-icon class="card-icon" icon="SettingsIcon"/>
            <span class="card-title">Conexões</span>
          </div>

          <div class="inline-flex gap-2">
            <vx-tooltip v-if="!shouldDisableCards && canSee('instances-add')" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Adicionar Conexão">
              <feather-icon @click="openInstanceCreationModal()" icon="PlusIcon"/>
            </vx-tooltip>
            <vx-tooltip v-if="!shouldDisableCards" class="cursor-pointer" delay=".2s" color="gray" position="bottom" text="Atualizar listagem">
              <feather-icon @click="loadFeatures()" icon="RefreshCwIcon"/>
            </vx-tooltip>
          </div>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <InstancesModal
            ref="instanceModal"
            :token="token"
            :isPopup="false"
            :isActive="false"
            :tenantName="tenantName"
            @instances="instancesLoaded"
            @createInstance="openInstanceCreationModal"
          />
        </div>
        <div v-else-if="shouldDisableCards" class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>

      <!-- CARD — Relatórios -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header">
          <feather-icon class="card-icon" icon="FileTextIcon"/>
          <span class="card-title">Relatórios</span>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <div class="report-buttons">
            <div class="button-group" v-if="isFuncionalUser">
              <vs-button color="primary" type="border" @click="downloadModals.webhooks = true">
                Disparos (Funcional)
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="downloadModals.attendances = true">
                Atendimentos/24h (Custos Cloud)
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('attendance')">
                Atendimentos
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('maria')">
                Atendimentos Maria
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('campaign')">
                Campanhas
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('operator')">
                Operadores
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('order')">
                Pedidos
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('grade')">
                Avaliação
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="openProductivityReport('loginFailures')">
                Falhas de Login
              </vs-button>
            </div>
          </div>
        </div>
        <div v-else class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>

      <!-- CARD — Scripts -->
      <div class="settings-card" :class="{ 'card-disabled': shouldDisableCards }">
        <div class="card-header">
          <feather-icon class="card-icon" icon="FileTextIcon"/>
          <span class="card-title">Scripts</span>
          <span v-if="shouldDisableCards" class="disabled-badge">Indisponível</span>
        </div>
        <div class="card-divider"></div>
        <div v-if="!shouldDisableCards" class="card-content">
          <div class="report-buttons">
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.syncAnalytics = true">
                Sincronizar Analytics
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.diagnoseInstance = true">
                Dr. de Conexão
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.restartCampaign = true">
                Reiniciar Campanha
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.finishedTicketsRank = true">
                Identificar Encerramento em Massa
              </vs-button>
            </div>
            <div class="button-group">
              <vs-button color="primary" type="border" @click="scriptModals.undoFinishedTickets = true">
                Desfazer Encerramento em Massa
              </vs-button>
            </div>
          </div>
        </div>
        <div v-else class="card-content disabled-content">
          <p class="disabled-message">Esta funcionalidade está indisponível.</p>
        </div>
      </div>
    </div>

    <vs-popup title="Confirmar Ação" :active.sync="tenantModal.confirmAction">
      <p class="mt-0 pt-0 font-medium">Clique em "{{ action }}" para confirmar a ação:</p>
      <div class="d-flex justify-end">
        <vs-button type="filled" @click="updateTenant(); tenantModal.confirmAction=false"> {{ action }} </vs-button>
      </div>
    </vs-popup>

    <CreateInstanceModal
      :tenantName="tenantName"
      :isActive="tenantModal.createInstance"
      @isActiveFalse="tenantModal.createInstance=false"
      @successfullyCreated="instanceSuccessfullyCreated"
    />

    <AttendancesReport
      :isActive="downloadModals.attendances"
      :websiteId="tenantData ? tenantData.website_id : null"
      @input="downloadModals.attendances = $event"
      @isActiveFalse="downloadModals.attendances=false"
    />

    <WebhooksReport
      :isActive="downloadModals.webhooks"
      :tenantName="tenantName"
      @close="downloadModals.webhooks = false"
      @isActiveFalse="downloadModals.webhooks=false"
    />

    <OrdersReport
      :isActive="downloadModals.orders"
      @input="downloadModals.orders = $event"
      @isActiveFalse="downloadModals.orders=false"
    />

    <InstanceLogsReport
      :isActive="downloadModals.instanceLogs"
      @input="downloadModals.instanceLogs = $event"
      @isActiveFalse="downloadModals.instanceLogs=false"
    />

    <SyncAnalytics
      :tenantName="tenantName"
      :isActive="scriptModals.syncAnalytics"
      @input="scriptModals.syncAnalytics = $event"
      @isActiveFalse="scriptModals.syncAnalytics=false"
    />

    <ProductivityReports
      :isActive="productivityReportModal.active"
      :reportType="productivityReportModal.type"
      @isActiveFalse="productivityReportModal.active = false"
    />

    <DiagnoseInstance
      :tenantName="tenantName"
      :instances="instances"
      :isActive="scriptModals.diagnoseInstance"
      @input="scriptModals.diagnoseInstance = $event"
      @isActiveFalse="scriptModals.diagnoseInstance = false"
    />

    <RestartCampaign
      :tenantName="tenantName"
      :isActive="scriptModals.restartCampaign"
      @input="scriptModals.restartCampaign = $event"
      @isActiveFalse="scriptModals.restartCampaign = false"
    />

    <FinishedTicketsRank
      :tenantName="tenantName"
      :isActive="scriptModals.finishedTicketsRank"
      @input="scriptModals.finishedTicketsRank = $event"
      @isActiveFalse="scriptModals.finishedTicketsRank = false"
    />

    <UndoFinishedTickets
      :tenantName="tenantName"
      :isActive="scriptModals.undoFinishedTickets"
      @input="scriptModals.undoFinishedTickets = $event"
      @isActiveFalse="scriptModals.undoFinishedTickets = false"
    />
  </div>
</template>

<script>
import jwt from "jsonwebtoken";
import {mapGetters} from 'vuex';
import {availablePlans, hasPermissionBackOffice} from "@/utils";
import InstancesModal from "@/views/Tenants/InstancesList.vue";
import CreateInstanceModal from "@/views/Tenants/CreateInstance.vue";
import UpdateTenantModal from "@/views/Tenants/UpdateTenant.vue";
import AttendancesReport from "../configs/AttendancesReport.vue";
import WebhooksReport from "../configs/WebhooksReport.vue";
import OrdersReport from "../configs/OrdersReport.vue";
import InstanceLogsReport from "../configs/InstanceLogsReport.vue";
import SyncAnalytics from "@/views/configs/SyncAnalytics.vue";
import ProductivityReports from "../configs/ProductivityReports.vue";
import DiagnoseInstance from "../configs/DiagnoseInstance.vue";
import RestartCampaign from "../configs/RestartCampaign.vue";
import FinishedTicketsRank from "../configs/FinishedTicketsRank.vue";
import UndoFinishedTickets from "../configs/UndoFinishedTickets.vue";

export default {
  name: 'TenantInfo',
  components: {
    SyncAnalytics,
    UpdateTenantModal,
    CreateInstanceModal,
    InstancesModal,
    AttendancesReport,
    WebhooksReport,
    OrdersReport,
    InstanceLogsReport,
    ProductivityReports,
    DiagnoseInstance,
    RestartCampaign,
    FinishedTicketsRank,
    UndoFinishedTickets
  },
  computed: {
    ...mapGetters(['token']),
    isTenantDeleted() {
      return this.tenantData && this.tenantData.deleted_at !== null;
    },
    wrongBackoffice() {
      return this.tenantData && this.tenantData.is_beta && !window.location.host.includes('beta');
    },
    shouldDisableCards() {
      return !this.isOriginBackoffice || !this.tenantData || (this.isTenantDeleted || this.wrongBackoffice);
    },
    isOriginBackoffice(){
      return process.env.VUE_APP_ORIGIN_BACKOFFICE && process.env.VUE_APP_ORIGIN_BACKOFFICE === 'true'
    }
  },
  data() {
    return {
      action: '',
      tenantName: '',
      tenantData: null,
      isFuncionalUser: false,
      instances: [],
      tenantModal: {
        confirmAction: false,
        createInstance: false,
      },
      dashSettings: {
        enabledPlan: null,
        activeFeatures: [],
        defaultPlans: availablePlans()
      },
      downloadModals: {
        orders: false,
        webhooks: false,
        attendances: false,
        instanceLogs: false
      },
      scriptModals: {
        syncAnalytics: false,
        diagnoseInstance: false,
        restartCampaign: false,
        finishedTicketsRank: false,
        undoFinishedTickets: false
      },
      productivityReportModal: {
        active: false,
        type: null
      }
    }
  },
  methods: {
    canSee(word) {
      return hasPermissionBackOffice(`tenant-list-${word}`);
    },
    openInstanceCreationModal() {
      this.tenantModal.createInstance = true;
    },
    instancesLoaded(value){
      this.instances = value
    },
    instanceSuccessfullyCreated() {
      this.tenantModal.createInstance = false;
      this.$refs['instanceModal'].loadInstances();
    },
    confirmAction() {
      this.action = this.tenantData.deleted_at ? 'Restaurar' : 'Desativar';
      this.tenantModal.confirmAction = true;
    },
    async redirectToLink() {
      const tenant = this.tenantData.fqdn.split(".")[0];
      const tokenPayload = { tenant: tenant, exp: Math.floor(Date.now() / 1000) + (3 * 60) };

      const baseUrl = `${process.env.VUE_APP_PEDBOT_ADMIN_URL}/${tenant}/login`;
      const access_key = jwt.sign(tokenPayload, 'GshW1upIsPpa9EQ');
      const access_token = process.env.VUE_APP_INTEGRATION_TOKEN;

      const params = new URLSearchParams();
      params.append('access_token', access_token);
      params.append('access_key', access_key);

      const url = `${baseUrl}?${params.toString()}`;
      window.open(url, '_blank');
    },
    updateTenant() {
      const action = this.tenantData.deleted_at ? 'restoreTenant' : 'deleteTenant';
      this.$store.dispatch(action, this.tenantData.id, this.token).then(() => {
        this.loadTenantData();
        this.$vs.notify({
          color: 'success',
          title: 'Sucesso!',
          text: `Tenant ${this.tenantData.deleted_at ? 'Restaurado' : 'Deletado'} com Sucesso!`
        });
      }).catch(exception => {
        console.error(exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao realizar a ação! Entre em contato com o time técnico.'
        });
      });
    },
    loadTenantData() {
      this.$store.dispatch('fetchPaginatedTenants', {
        page: 1,
        limit: 1,
        sortName: 'deleted_at',
        sortType: 'DESC',
        token: this.token,
        filterUsers: false,
        search: this.tenantName + '.'
      }).then(response => {
        this.tenantData = response.items[0];
        this.loadFeatures()
      }).catch(error => {
        console.error('Error loading tenant data:', error);
        this.$vs.notify({color: 'danger', title: 'Erro!', text: 'Erro ao carregar dados do tenant.'});
      });
    },
    loadFeatures() {
      this.$store.dispatch('getFeatures', {
        token: this.token,
        tenantName: this.tenantName
      }).then(response => {
        const features = (response && response.data ? response.data : []);
        this.dashSettings.enabledPlan = this.tenantData.plan;
        this.dashSettings.activeFeatures = features.filter(item => item.name !== 'websocket');
        this.$refs['instanceModal'].loadInstances();
      }).catch(exception => console.error(exception));
    },
    setState(newVal, features, name = null) {
      if (name){ this[name] = newVal; }
      if (features && Object.keys(features).length) {
        this.$store.dispatch('updateFeatures', {features: features, tenantName: this.tenantName, token: this.token}).then(() => {
          this.$vs.notify({ color: 'success',
            title: 'Sucesso!',
            text: `Features ativadas com sucesso!`
          });
        }).catch(exception => {
          console.error(exception)
          this.$vs.notify({
            color: 'danger',
            title: 'Erro!',
            text: 'Ocorreu um erro ao realizar a ação! Entre em contato com o time técnico.'
          });
        });
      }
    },
    updatePlan({plan, user, ticket}) {
      this.$vs.loading();

      if ( !plan ) {
        this.$vs.loading.close();
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Informe um valor válido para o Plano.'
        });

        return;
      }

      plan = plan.toUpperCase();
      this.$store.dispatch('updateTenantPlan', { token: this.token, id: this.tenantData.id, plan: plan, user: user, ticket: ticket}).then(() => {
        this.loadTenantData();
        this.$vs.notify({
          color: 'success',
          title: 'Sucesso!',
          text: `Plano do Tenant atualizado com sucesso.`
        });
      }).catch(exception => {
        console.error(exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao realizar a ação! Entre em contato com o time técnico.'
        });
      });

      this.$vs.loading.close();
    },
    openProductivityReport(type) {
      this.productivityReportModal.type = type;
      this.productivityReportModal.active = true;
    }
  },
  created() {
    this.tenantName = this.$route.params.tenantName;
    this.loadTenantData();
    this.$store.dispatch('isFuncionalUser', this.token).then(response => this.isFuncionalUser = !!response.data);
  }
}
</script>

<style lang="scss" scoped>
.info {
  padding: 2rem 50px;
  background-color: #ffffff;
  min-height: 100vh;
}

.settings-container {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
}

.settings-card {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e8e8e8;
  overflow: hidden;
}

.align-items-center{
  align-items: center !important;
}

.card-header {
  display: flex;
  padding: 1rem;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
}

.card-icon {
  width: 16px;
  height: 16px;
  color: #666;
  margin-right: 0.5rem;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.card-divider {
  height: 1px;
  background-color: #e8e8e8;
}

.card-content {
  padding: 1rem;
}

.switch-row {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.switch-label {
  margin-left: 0.5rem;
  font-size: 13px;
  color: #666;
}

.tenant-info-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 1rem;
}

.tenant-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 11px;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.status-active {
  color: #2e7d32;
  font-weight: 600;
}

.status-inactive {
  color: #666;
}

.status-warning {
  color: #f57c00;
  font-weight: 600;
}

.report-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.card-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.disabled-badge {
  margin-left: auto;
  font-size: 11px;
  color: #f44336;
  font-weight: 600;
  background-color: #ffebee;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.disabled-content {
  text-align: center;
  padding: 2rem 1rem;
}

.disabled-message {
  color: #666;
  font-size: 13px;
  margin: 0;
  font-style: italic;
}
</style>
